import ActionButton from "@/components/buttons/ActionButton";
import HiddenInput from "@/components/form/components/HiddenInput";
import ActionTextIcon from "@/components/icons/ActionTextIcon";
import DeleteIcon from "@/components/icons/DeleteIcon";
import { selectStandardProps } from "@/utils/componentUtils";
import { resolveFormValidationError } from "@/utils/formUtils";
import { isDefined } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { Alert, Col, Form, Input, Row, Select } from "antd";
import { FormInstance, Rule } from "antd/lib/form";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import t from "../../../../../app/i18n";
import { rowGutter } from "../../../../../common/constants";
import { ClientRepresentativeFunction, ClientType } from "../../../enums";
import { useClientSearch } from "../../../hooks";
import { CLIENT_ROUTE_PATHS } from "../../../paths";
import {
  ClientBase,
  CreateUpdateClient,
  CreateUpdateClientRepresentative,
  CreateUpdateLegalClient,
  LegalClient,
} from "../../../types";
import { ClientTypeTagWithName } from "../../tags/ClientTypeTagWithName";

interface Props {
  form: FormInstance<CreateUpdateClient>;
  initialClient?: LegalClient;
  colSpan: number;
}

const noRepeatedRepresentative: Rule = ({ getFieldValue }) => ({
  validator: (_, value) =>
    value &&
    (getFieldValue(["representatives"]) || [])
      .map((r: CreateUpdateClientRepresentative) => r.representativePin)
      .filter((pin: string) => pin === value).length > 1
      ? Promise.reject(t("client.validations.duplicateRepresentative"))
      : Promise.resolve(),
});

export const ClientRepresentativesFormPart = ({ form, initialClient, colSpan }: Props) => {
  const { t } = useTranslation();

  const [selectedSearchIndex, setSelectedSearchIndex] = useState<number>();
  const [representatives, setRepresentatives] = useState<(Partial<ClientBase> | boolean)[]>(
    initialClient?.representatives ? initialClient.representatives.map((r) => r.representative) : [],
  );

  const clientSearch = useClientSearch();

  useEffect(() => {
    if (
      isDefined(selectedSearchIndex) &&
      clientSearch.result.keyword === form.getFieldValue(["representatives", selectedSearchIndex, "representativePin"])
    ) {
      const updatedRepresentatives = [...representatives];

      if (clientSearch.result.data) {
        updatedRepresentatives[selectedSearchIndex] = clientSearch.result.data;
        const formValue = [...form.getFieldValue(["representatives"])];
        formValue[selectedSearchIndex].representativeId = clientSearch.result.data.id;
        form.setFieldsValue({
          representatives: formValue as CreateUpdateClientRepresentative[],
        } as CreateUpdateLegalClient);
      } else {
        updatedRepresentatives[selectedSearchIndex] = true;
      }

      setRepresentatives(updatedRepresentatives);
    }
  }, [clientSearch.result]);

  useEffect(() => {
    return () => {
      clientSearch.onResultDelete();
    };
  }, []);

  const handleClientSearchSubmit = (value: string, index: number) => {
    form
      .validateFields([["representatives", index, "representativePin"]])
      .then(() => clientSearch.onSearch({ clientType: ClientType.NATURAL, keyword: value }))
      .catch(resolveFormValidationError);
  };

  const handleClientSearchChange = (value: string, index: number) => {
    if (representatives[index]) {
      const updatedRepresentatives: any = [...representatives];
      updatedRepresentatives[index] = undefined;
      setRepresentatives(updatedRepresentatives);

      const formValue = [...form.getFieldValue(["representatives"])];
      formValue[index].id = undefined;
      formValue[index].optimisticLockVersion = undefined;
      formValue[index].representativeId = undefined;
      form.setFieldsValue({
        representatives: formValue as CreateUpdateClientRepresentative[],
      } as CreateUpdateLegalClient);
    }

    handleClientSearchSubmit(value, index);
  };

  const handleRowDelete = (index: number, remove: (index: number | number[]) => void) => {
    const updatedRepresentatives = [...representatives];
    updatedRepresentatives.splice(index, 1);
    setRepresentatives(updatedRepresentatives);
    remove(index);
  };

  return (
    <Form.List name={["representatives"]}>
      {(fields, { add, remove }) => (
        <>
          {fields.map(({ key, ...field }) => (
            <React.Fragment key={key}>
              <HiddenInput name={[field.name, "id"]} />
              <HiddenInput name={[field.name, "optimisticLockVersion"]} />
              <HiddenInput name={[field.name, "representativeId"]} />

              <Row gutter={rowGutter}>
                <Col span={colSpan}>
                  <Form.Item
                    {...field}
                    name={[field.name, "function"]}
                    label={t("client.enums.representativeFunction._label")}
                    rules={[validations.notNull]}
                  >
                    <Select
                      {...selectStandardProps}
                      options={Object.keys(ClientRepresentativeFunction).map((func) => ({
                        value: func,
                        label: t("client.enums.representativeFunction." + func),
                      }))}
                    />
                  </Form.Item>
                </Col>

                <Col span={colSpan}>
                  <Form.Item
                    {...field}
                    name={[field.name, "representativePin"]}
                    label={t("client.attrs.representatives.representativePin")}
                    rules={[validations.notBlank, validations.pin, noRepeatedRepresentative]}
                    validateFirst
                  >
                    <Input.Search
                      loading={clientSearch.inProgress && selectedSearchIndex === field.name}
                      onFocus={() => setSelectedSearchIndex(field.name)}
                      onSearch={(value) => handleClientSearchSubmit(value, field.name)}
                      onChange={(event) => handleClientSearchChange(event.target.value, field.name)}
                    />
                  </Form.Item>
                </Col>

                <Col span={colSpan + 3}>
                  {representatives[field.name] &&
                    (representatives[field.name] === true ? (
                      <Alert
                        showIcon
                        type="warning"
                        title={
                          <span>
                            {t("client.helpers.representativeNotFound")}
                            <span style={{ paddingLeft: 10 }}>
                              <ActionTextIcon
                                path={CLIENT_ROUTE_PATHS.create.to}
                                target="_blank"
                                icon="user-add"
                                color="blue"
                                text={t("client.actions.createRepresentative")}
                              />
                            </span>
                          </span>
                        }
                      />
                    ) : (
                      <div style={{ marginTop: 32 }}>
                        <ClientTypeTagWithName
                          client={representatives[field.name] as ClientBase}
                          showLink
                          target="_blank"
                        />
                      </div>
                    ))}
                </Col>

                <Col span={1}>
                  <DeleteIcon onClick={() => handleRowDelete(field.name, remove)} />
                </Col>
              </Row>
            </React.Fragment>
          ))}

          <ActionButton icon="plus" onClick={() => add({ function: undefined, representativePin: undefined })}>
            {t("client.actions.addRepresentative")}
          </ActionButton>
        </>
      )}
    </Form.List>
  );
};
